/**
 * Temporary screen-scoped design tokens for the **Approvers and Signers** step
 * of Create Sign Request (`/create/request?type=others`, step 2).
 *
 * Mirrors the `STEP 2 : APPROVERS AND SIGNERS` block of
 * `Create Sign Request - Me & Others.dc.html` 1:1 — every colour, size, radius,
 * spacing and type value the step needs lives here so the components never
 * inline a literal. Once every screen is re-skinned these get promoted into
 * `src/theme` and this file is deleted (see UI_IMPLEMENTATION_RULES.md
 * § Temporary Screen Tokens).
 */

import {
  formField,
  inkfree,
  mobile,
  notification,
  signer,
  white,
} from "theme/colors";

const accent = signer.orange.main;
const accentHover = signer.orange.dark;
/**
 * Solid CTA / checked-state fills (buttons, checked checkboxes) — per the
 * standing brand-colour rule these are the app brand red, not `accent`
 * above, which stays orange for borders/underlines/text/icon strokes.
 */
const ctaBg = inkfree.main;
const ctaBgHover = inkfree.dark;
const ink = signer.ink;
const inkSoft = signer.text.secondary;
const muted = signer.text.faint;

/** Popover chrome shared by the add / role / security menus. */
const menu = {
  bg: white,
  border: signer.border.light,
  radius: "10px",
  shadow: "0 10px 30px rgba(33,21,71,.14)",
  padding: "6px",
  itemPadding: "9px 12px",
  itemRadius: "7px",
  itemHoverBg: signer.surface.muted,
  titleFontSize: "13px",
  titleFontWeight: 700,
  titleColor: ink,
  subFontSize: "11.5px",
  subColor: muted,
  /**
   * The name/email contact-suggestion dropdown: a name + email pair needs
   * more room than the role/security/delivery menus.
   */
  contactWidth: 340,
} as const;

/**
 * The design's `underline` input treatment — the default of its `inputStyle`
 * prop, and the one the reference screenshot uses.
 */
const input = {
  height: 38,
  memberHeight: 32,
  border: "none",
  underline: `1.5px solid ${signer.disabled}`,
  focusUnderline: `1.5px solid ${accent}`,
  radius: "0px",
  bg: "transparent",
  padding: "0 4px",
  nameFontSize: "14px",
  nameFontWeight: 600,
  emailFontSize: "14px",
  emailFontWeight: 500,
  memberFontSize: "13px",
  color: ink,
  placeholderColor: formField.placeholderMuted,
  /** Name column is 1fr, email 1.2fr in the design. */
  nameFlex: 1,
  emailFlex: 1.2,
  /**
   * Boxed treatment used by the signer name / email fields (the "same as image"
   * bordered inputs): a 1px rounded box rather than the underline above.
   */
  boxedHeight: 40,
  boxedBorder: `1px solid ${signer.disabled}`,
  boxedRadius: "8px",
  boxedPadding: "0 10px !important",
} as const;

export const recipientsTokens = {
  accent,
  accentHover,
  ctaBg,
  ctaBgHover,
  menu,
  input,

  /** Scroll body + centred content column. */
  page: {
    bg: white,
    maxWidth: 1220,
    padding: "26px 40px 80px",
  },

  /** checkbox | tabs | add-button row above the list. */
  toolbar: {
    marginBottom: "26px",
    leftGap: "9px",
    actionsGap: "10px",
    checkboxSize: 18,
    checkboxRadius: "4px",
    checkboxBorderWidth: "1.5px",
    checkboxBorderOff: mobile.base[100],
    checkboxBorderOn: accent,
    checkboxBgOff: white,
    checkboxBgOn: ctaBg,
    checkboxTickSize: 11,
    labelFontSize: "14px",
    labelFontWeight: 600,
    labelColor: ink,
    infoIconSize: 15,
    infoIconColor: muted,
    /** Explainer under the toolbar once ordering is on. */
    hintFontSize: "12.5px",
    hintColor: muted,
    hintMargin: "-8px 0 14px",
  },

  /** Signers / Approvers underline tabs. */
  tabs: {
    gap: "4px",
    padding: "10px 18px 12px",
    itemGap: "8px",
    indicatorWidth: "2.5px",
    indicatorActive: accent,
    indicatorIdle: "transparent",
    colorActive: accent,
    colorIdle: inkSoft,
    iconSize: 16,
    labelFontSize: "14px",
    labelFontWeight: 700,
    countMinWidth: 20,
    countHeight: 20,
    countRadius: "10px",
    countPadding: "0 5px",
    countBg: signer.surface.muted,
    countColor: inkSoft,
    countFontSize: "11px",
    countFontWeight: 700,
  },

  /** Split "Add signer" / "Add approver" button. */
  addButton: {
    height: 42,
    radius: "9px",
    bg: ctaBg,
    hoverBg: ctaBgHover,
    color: white,
    padding: "0 18px",
    fontSize: "14px",
    fontWeight: 700,
    gap: "8px",
    iconSize: 14,
    dividerColor: "rgba(255,255,255,.35)",
    caretWidth: 34,
    caretIconSize: 12,
    menuWidthSigner: 240,
    menuWidthApprover: 260,
    menuOffset: "48px",
  },

  /** "1 ▸ Level 1 — notified first ———— ⌃⌄ + Add signer here" strip. */
  levelHeader: {
    gap: "10px",
    margin: "6px 0 10px",
    numSize: 24,
    numFontSize: "12px",
    numFontWeight: 800,
    numColor: white,
    labelFontSize: "13px",
    labelFontWeight: 800,
    labelLetterSpacing: ".02em",
    labelColor: ink,
    subFontSize: "12px",
    subColor: muted,
    ruleColor: signer.border.light,
    moveWidth: 24,
    moveHeight: 22,
    moveRadius: "6px",
    moveColor: inkSoft,
    moveIconSize: 12,
    moveHoverBg: signer.surface.muted,
    moveDisabledOpacity: 0.25,
    addHeight: 26,
    addPadding: "0 11px",
    addRadius: "999px",
    addBorder: `1px dashed ${signer.disabled}`,
    addColor: inkSoft,
    addFontSize: "11.5px",
    addFontWeight: 700,
    addGap: "6px",
    addIconSize: 10,
  },

  /** One signer / approver card. */
  row: {
    marginBottom: "12px",
    minHeight: 66,
    gap: "14px",
    paddingRight: "14px",
    border: signer.border.light,
    radius: "10px",
    bg: white,
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    barWidth: 5,
    barRadius: "10px 0 0 10px",
    dragWidth: 22,
    dragHeight: 40,
    dragIconSize: 14,
    dragIconColor: muted,
    /** Stacked level up/down arrows inside the row. */
    levelMoveWidth: 22,
    levelMoveHeight: 16,
    levelMoveRadius: "5px",
    levelMoveIconSize: 11,
    deleteSize: 32,
    deleteRadius: "8px",
    deleteColor: muted,
    deleteIconSize: 15,
    deleteHoverBg: signer.orange.tint,
    /** Gap between the role / delivery-mode / security selects on row 2. */
    settingsRightGap: "10px",
  },

  /** Role + security triggers on the right of a signer row. */
  select: {
    height: 36,
    padding: "0 12px",
    gap: "8px",
    border: `1px solid ${signer.disabled}`,
    hoverBorder: accent,
    radius: "8px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: ink,
    bg: white,
    caretSize: 10,
    caretColor: inkSoft,
    shieldSize: 13,
    shieldIdleColor: muted,
    /** Role-trigger icon: pen (signer) vs eye (CC), per the design. */
    roleIconSize: 13,
    roleIconSignerColor: accent,
    roleIconCcColor: signer.purple.main,
    /** Delivery-mode trigger icon: link (send a link) vs monitor (in person). */
    deliveryIconSize: 13,
    deliveryIconColor: inkSoft,
    menuWidthRole: 230,
    menuWidthSecurity: 250,
    // Delivery captions ("Signer signs on the host's device, no email sent")
    // are longer than the role/security ones, so this menu is a touch wider.
    menuWidthDelivery: 300,
    menuOffset: "42px",
  },

  /** Access-code editor / confirmation and the OTP note under a row. */
  security: {
    blockMargin: "8px 0 0 24px",
    codeGap: "10px",
    codePadding: "8px 8px 8px 14px",
    codeRadius: "8px",
    codeBg: signer.warn.tint,
    codeBorder: signer.orange.progressTrack,
    codeIconSize: 14,
    codeLabelFontSize: "12px",
    codeLabelFontWeight: 700,
    codeLabelColor: ink,
    codeInputWidth: 220,
    codeInputHeight: 30,
    codeInputUnderline: "1.5px solid #E9C6A8",
    codeInputFontSize: "12.5px",
    codeInputFontWeight: 600,
    saveHeight: 30,
    savePadding: "0 16px",
    saveRadius: "7px",
    saveDisabledBg: "#F0AE95",
    saveFontSize: "12px",
    saveFontWeight: 700,
    savedPadding: "8px 14px",
    savedBg: signer.success.tint,
    savedBorder: "#CBE8D6",
    savedTickColor: "#3AA76D",
    maskedFontSize: "13px",
    maskedFontWeight: 800,
    maskedColor: inkSoft,
    maskedLetterSpacing: "2px",
    editFontSize: "12px",
    editFontWeight: 700,
    noteMargin: "7px 0 0 24px",
    noteGap: "8px",
    noteIconSize: 12,
    noteFontSize: "11.5px",
    noteFontWeight: 500,
    noteColor: muted,
  },

  /**
   * SMS Delivery — the "Deliver via" channel checkboxes inside a signer row, the
   * inline delivery-phone field shown when SMS / WhatsApp is picked, and the
   * phone-authentication banner (security option) below the card. The warm
   * palette (#FFF7ED / #F6DACB / #E9C6A8) mirrors `security`.
   */
  delivery: {
    // "Deliver via [ ] Email [ ] Text (SMS) [ ] WhatsApp" line inside the card.
    linePadding: "0 14px 14px 44px",
    lineGap: "16px",
    labelFontSize: "10.5px",
    labelLetterSpacing: ".06em",
    labelColor: muted,
    labelFontWeight: 700,
    optionGap: "8px",
    boxSize: 17,
    boxRadius: "5px",
    boxBorderWidth: "1.5px",
    boxBorderOff: mobile.base[100],
    boxBorderOn: accent,
    boxBgOff: white,
    boxBgOn: ctaBg,
    tickSize: 11,
    optionFontSize: "12.5px",
    optionFontWeight: 600,
    optionColor: ink,
    // Coverage caveat beside a channel label ("(US and Canada only)").
    // Deliberately smaller and muted: it qualifies the option, it isn't part
    // of its name.
    optionNoteFontSize: "11px",
    optionNoteFontWeight: 500,
    optionNoteColor: muted,
    // Inline delivery-phone field (in the row, shown when SMS / WhatsApp is on).
    // The control itself is the shared CustomContactNumberInput; only its width
    // in the row is set here.
    inlineFlex: 0.9,
    inlineMinWidth: 200,
    // Warm banner chrome (phone-authentication row below the card).
    boxMargin: "8px 0 0 24px",
    boxRadius2: "8px",
    boxBg: signer.warn.tint,
    boxBorder: signer.orange.progressTrack,
    iconSize: 14,
    iconColor: accent,
    titleFontSize: "12px",
    titleFontWeight: 700,
    titleColor: ink,
    // Auth phone (shared CustomContactNumberInput) width inside the banner.
    authPhoneMinWidth: 220,
    // Phone-authentication banner note + "Same as delivery number" chip.
    authPadding: "8px 14px",
    authNoteFontSize: "10.5px",
    authNoteColor: muted,
    sameGap: "6px",
    sameHeight: 28,
    samePadding: "0 11px",
    sameRadius: "999px",
    sameBorder: "1.5px dashed #E9C6A8",
    sameColor: accent,
    sameFontSize: "11.5px",
    sameFontWeight: 700,
    sameIconSize: 11,
  },

  /** "Wait N day(s) before next level" slot between levels. */
  delay: {
    gap: "12px",
    margin: "2px 0 14px",
    paddingLeft: "10px",
    stemWidth: "2px",
    stemHeight: 26,
    stemColor: signer.border.light,
    pillHeight: 34,
    pillPadding: "0 6px 0 12px",
    pillRadius: "999px",
    pillBg: signer.warn.tint,
    pillBorder: signer.orange.progressTrack,
    pillGap: "10px",
    clockSize: 14,
    textFontSize: "12.5px",
    textFontWeight: 700,
    textColor: ink,
    stepGap: "3px",
    stepSize: 24,
    stepRadius: "6px",
    stepBorder: signer.orange.border,
    stepFontSize: "14px",
    stepFontWeight: 800,
    stepHoverBg: signer.orange.tint,
    removeIconSize: 11,
    addHeight: 32,
    addPadding: "0 14px",
    addRadius: "999px",
    addBorder: `1.5px dashed ${signer.disabled}`,
    addColor: inkSoft,
    addFontSize: "12.5px",
    addFontWeight: 700,
    addGap: "7px",
    addIconSize: 13,
  },

  /** Drop target that promotes a dragged signer into a brand-new last level. */
  newLevelZone: {
    height: 56,
    radius: "12px",
    borderWidth: "2px",
    borderIdle: signer.disabled,
    borderActive: accent,
    colorIdle: muted,
    colorActive: accent,
    bgIdle: white,
    bgActive: signer.orange.faintest,
    fontSize: "13px",
    fontWeight: 700,
    label: "Drop here to create a new level",
  },

  /** Outline drawn around a level while a signer is dragged over it. */
  levelDropTarget: {
    radius: "12px",
    outline: `2px dashed ${accent}`,
    outlineOffset: "3px",
    bg: signer.orange.faintest,
  },

  /** Dashed placeholder shown when a tab has no rows yet. */
  empty: {
    border: `1.5px dashed ${signer.disabled}`,
    radius: "12px",
    padding: "44px",
    gap: "12px",
    titleFontSize: "14px",
    titleFontWeight: 600,
    titleColor: inkSoft,
    buttonHeight: 38,
    buttonPadding: "0 20px",
    buttonRadius: "9px",
    buttonFontSize: "13px",
    buttonFontWeight: 700,
  },

  /**
   * Host-email callout, shown above the signer list once any signer is
   * in-person. Single-row pill (icon + label + inline input), same footprint
   * as the security `codeEditor` row — the explainer moves into a tooltip on
   * the info glyph instead of a second line of body text.
   */
  hostEmail: {
    gap: "8px",
    padding: "6px 10px 6px 12px",
    radius: "8px",
    bg: signer.warn.tint,
    border: signer.orange.progressTrack,
    marginBottom: "10px",
    iconSize: 14,
    iconColor: accent,
    labelFontSize: "12.5px",
    labelFontWeight: 700,
    labelColor: ink,
    infoIconSize: 13,
    infoIconColor: muted,
    fieldWidth: 210,
    fieldHeight: 30,
  },

  /**
   * SMS credit callout, above the signer list once any signer is set to
   * receive the request (or verify) by text. Two tones, both reusing palette
   * groups rather than introducing a colour: amber while the wallet is merely
   * low, red once this send won't fit. Same pill footprint as `hostEmail`, but
   * full width — it carries a sentence and an action, not a field.
   */
  smsCredit: {
    gap: "10px",
    padding: "10px 14px",
    radius: "9px",
    marginBottom: "10px",
    iconSize: 15,
    textFontSize: "12.5px",
    textFontWeight: 600,
    linkFontSize: "12.5px",
    linkFontWeight: 700,
    low: {
      bg: signer.warn.tint,
      border: signer.warn.border,
      text: signer.warn.text,
      icon: signer.warn.main,
    },
    blocked: {
      bg: notification.error.bg,
      border: notification.error.border,
      text: notification.error.text,
      icon: notification.error.icon,
    },
  },

  /** Approvers tab specifics. */
  approvers: {
    stackGap: "12px",
    bannerGap: "10px",
    bannerPadding: "11px 16px",
    bannerRadius: "9px",
    bannerBg: signer.surface.muted,
    bannerMarginBottom: "4px",
    bannerIconSize: 15,
    bannerFontSize: "12.5px",
    bannerFontWeight: 500,
    bannerColor: inkSoft,
    barColor: ink,
    badgeSize: 26,
    badgeBg: ink,
    badgeColor: white,
    badgeFontSize: "12px",
    badgeFontWeight: 800,
    typeIconSize: 17,
    typeIconColor: muted,
    groupNameFontWeight: 700,
    ruleHeight: 30,
    rulePadding: "0 12px",
    ruleRadius: "999px",
    ruleBg: signer.surface.muted,
    ruleHoverBg: signer.border.light,
    ruleFontSize: "12px",
    ruleFontWeight: 700,
    ruleColor: inkSoft,
    ruleGap: "7px",
    ruleDotSize: 7,
    membersGap: "8px",
    membersPadding: "4px 16px 14px 46px",
    membersBorderTop: signer.surface.muted,
    memberGap: "12px",
    memberMarginTop: "8px",
    bulletSize: 6,
    bulletColor: signer.disabled,
    memberRemoveSize: 26,
    memberRemoveRadius: "7px",
    memberRemoveIconSize: 11,
    addMemberGap: "7px",
    addMemberMarginTop: "6px",
    addMemberFontSize: "12.5px",
    addMemberFontWeight: 700,
    addMemberIconSize: 12,
  },
} as const;

/**
 * Level / signer-bar palette. The design cycles its own six hues rather than
 * the app-wide `SIGNER_COLORS`, and the level badge and the row's left bar must
 * agree, so both read from here.
 */
export const recipientsLevelPalette = [
  "#E8474B",
  "#F5B21E",
  "#3AA76D",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
] as const;

/** Bar / badge colour for the nth level (or nth row when ordering is off). */
export const getRecipientsLevelColor = (index: number) =>
  recipientsLevelPalette[
    ((index % recipientsLevelPalette.length) + recipientsLevelPalette.length) %
      recipientsLevelPalette.length
  ];

export default recipientsTokens;
